import { Box, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch } from 'react-router-dom';
import styled from 'styled-components';

import useApi from '../../hooks/api';
import useIsMobile from '../../hooks/isMobile';
import useProfile from '../../hooks/profile';
import {
    CustomerConfigPageDefs,
    PageDefs,
    accidentsMetricsPageDefs,
    accidentsPageDefs,
    alertsPageDefs,
    coachingPageDefs,
    csaScoresPageDefs,
    driversPageDefs,
    eventFeedPageDefs,
    eventsPageDefs,
    findVideoPageDefs,
    flattenRoles,
    fleetDriversPageDefs,
    fleetOverviewPageDefs,
    fleetVehiclesPageDefs,
    isAllowedCustomer,
    lossRunsPageDefs,
    malfunctionsPageDefs,
    operationsPageDefs,
    organizationSettingsPageDefs,
    otaPageDefs,
    policiesPageDefs,
    reportsPageDefs,
    safetyMetricsPageDefs,
    userSettingsPageDefs,
    usersPageDefs,
} from '../../utils/Pages';
import { DEFAULT_PAGE } from '../Navigator/Navigator';
import AccidentComponent from './Accident';
import CoachComponent from './Coaching/CoachComponent';
import CustomerConfigPageComponent from './CustomerConfig';
import DriversComponent from './Drivers/DriversComponent';
import EventComponent from './Event/Event';
import FeedComponent from './Feed/FeedComponent';
import FindVideoComponent from './FindVideo';
import FleetDriversComponent from './FleetDrivers/FleetDriversComponent';
import FleetVehiclesComponent from './FleetVehicles/FleetVehiclesComponent';
import LossRunsComponent from './LossRuns/LossRunsComponent';
import MalfunctionsComponent from './Malfunctions/MalfunctionsComponent';
import MetricsContent from './Metrics/MetricsContent';
import DeviceComponent from './OTA/DeviceComponent';
import OperationsTable from './Operations/OperationsTable';
import OrganizationSettings from './OrganizationSettings/OrganizationSettings';
import FleetOverview from './Overview/FleetOverview';
import PolicyComponent from './Policies/PolicyComponent';
import ReportComponent from './Reports/ReportComponent';
import UserSettings from './UserSettings/UserSettings';
import UsersComponent, { OE_ROLES } from './Users/UsersComponent';

const ContentDiv = styled.div`
    overflow-y: auto;
    width: 100%;
    height: 100%;
`;

const ContentPaddedDiv = styled.div`
    padding: 1em;
    padding-bottom: 0;
    overflow-y: auto;
    width: 100%;
    height: 100%;
`;

interface ProtectedRouteProps {
    pageDef: PageDefs;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: React.ReactElement<any, any> | null;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ pageDef, children }: ProtectedRouteProps) => {
    const { profile } = useProfile();
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const roles = flattenRoles(pageDef);

    if (roles.includes(profile.role) && isAllowedCustomer(pageDef, profile)) {
        if (isMobile && !pageDef.mobileSupport && !OE_ROLES.includes(profile.role)) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <Typography variant="overline">{t('content.not_supported')}</Typography>
                </Box>
            );
        }
        return children;
    } else {
        return <Redirect to={profile.home_page ? profile.home_page : DEFAULT_PAGE} />;
    }
};

const Content: React.FC = () => {
    const { profile } = useProfile();
    const { isAuthenticated } = useApi();

    if (!isAuthenticated || profile.user_id === 0) {
        return null;
    }

    return (
        <Switch>
            <Route path={fleetOverviewPageDefs.path}>
                <ProtectedRoute pageDef={fleetOverviewPageDefs}>
                    <ContentDiv>
                        <FleetOverview />
                    </ContentDiv>
                </ProtectedRoute>
            </Route>
            <Route path={malfunctionsPageDefs.path}>
                <ProtectedRoute pageDef={malfunctionsPageDefs}>
                    <ContentDiv>
                        <MalfunctionsComponent />
                    </ContentDiv>
                </ProtectedRoute>
            </Route>
            <Route path={fleetDriversPageDefs.path}>
                <ProtectedRoute pageDef={fleetDriversPageDefs}>
                    <ContentDiv>
                        <FleetDriversComponent />
                    </ContentDiv>
                </ProtectedRoute>
            </Route>
            <Route path={fleetVehiclesPageDefs.path}>
                <ProtectedRoute pageDef={fleetVehiclesPageDefs}>
                    <ContentDiv>
                        <FleetVehiclesComponent />
                    </ContentDiv>
                </ProtectedRoute>
            </Route>
            <Route path={findVideoPageDefs.path}>
                <ProtectedRoute pageDef={findVideoPageDefs}>
                    <ContentDiv>
                        <FindVideoComponent />
                    </ContentDiv>
                </ProtectedRoute>
            </Route>
            <Route path={eventFeedPageDefs.path}>
                <ProtectedRoute pageDef={eventFeedPageDefs}>
                    <ContentDiv>
                        <FeedComponent />
                    </ContentDiv>
                </ProtectedRoute>
            </Route>
            <Route path={driversPageDefs.path}>
                <ProtectedRoute pageDef={driversPageDefs}>
                    <ContentPaddedDiv>
                        <DriversComponent />
                    </ContentPaddedDiv>
                </ProtectedRoute>
            </Route>
            <Route path={coachingPageDefs.path}>
                <ProtectedRoute pageDef={coachingPageDefs}>
                    <ContentDiv>
                        <CoachComponent />
                    </ContentDiv>
                </ProtectedRoute>
            </Route>
            <Route path={eventsPageDefs.path}>
                <ProtectedRoute pageDef={eventsPageDefs}>
                    <ContentDiv>
                        <EventComponent />
                    </ContentDiv>
                </ProtectedRoute>
            </Route>
            <Route exact path={safetyMetricsPageDefs.path}>
                <ProtectedRoute pageDef={safetyMetricsPageDefs}>
                    <ContentPaddedDiv>
                        <MetricsContent />
                    </ContentPaddedDiv>
                </ProtectedRoute>
            </Route>
            <Route path={accidentsMetricsPageDefs.path}>
                <ProtectedRoute pageDef={accidentsMetricsPageDefs}>
                    <ContentPaddedDiv>
                        <MetricsContent />
                    </ContentPaddedDiv>
                </ProtectedRoute>
            </Route>
            <Route path={csaScoresPageDefs.path}>
                <ProtectedRoute pageDef={csaScoresPageDefs}>
                    <ContentPaddedDiv>
                        <MetricsContent />
                    </ContentPaddedDiv>
                </ProtectedRoute>
            </Route>
            <Route path={policiesPageDefs.path}>
                <ProtectedRoute pageDef={policiesPageDefs}>
                    <ContentPaddedDiv>
                        <PolicyComponent />
                    </ContentPaddedDiv>
                </ProtectedRoute>
            </Route>
            <Route path={accidentsPageDefs.path}>
                <ProtectedRoute pageDef={accidentsPageDefs}>
                    <ContentDiv>
                        <AccidentComponent />
                    </ContentDiv>
                </ProtectedRoute>
            </Route>
            <Route path={lossRunsPageDefs.path}>
                <ProtectedRoute pageDef={lossRunsPageDefs}>
                    <ContentDiv>
                        <LossRunsComponent />
                    </ContentDiv>
                </ProtectedRoute>
            </Route>
            <Route path={usersPageDefs.path}>
                <ProtectedRoute pageDef={usersPageDefs}>
                    <ContentPaddedDiv>
                        <UsersComponent />
                    </ContentPaddedDiv>
                </ProtectedRoute>
            </Route>
            <Route path={reportsPageDefs.path}>
                <ProtectedRoute pageDef={reportsPageDefs}>
                    <ContentPaddedDiv>
                        <ReportComponent key="REPORTS" type="REPORTS" />
                    </ContentPaddedDiv>
                </ProtectedRoute>
            </Route>
            <Route path={alertsPageDefs.path}>
                <ProtectedRoute pageDef={alertsPageDefs}>
                    <ContentPaddedDiv>
                        <ReportComponent key="ALERTS" type="ALERTS" />
                    </ContentPaddedDiv>
                </ProtectedRoute>
            </Route>
            <Route path={operationsPageDefs.path}>
                <ProtectedRoute pageDef={operationsPageDefs}>
                    <ContentPaddedDiv>
                        <OperationsTable />
                    </ContentPaddedDiv>
                </ProtectedRoute>
            </Route>
            <Route path={otaPageDefs.path}>
                <ProtectedRoute pageDef={otaPageDefs}>
                    <ContentPaddedDiv>
                        <DeviceComponent />
                    </ContentPaddedDiv>
                </ProtectedRoute>
            </Route>
            <Route path={CustomerConfigPageDefs.path}>
                <ProtectedRoute pageDef={CustomerConfigPageDefs}>
                    <ContentPaddedDiv>
                        <CustomerConfigPageComponent />
                    </ContentPaddedDiv>
                </ProtectedRoute>
            </Route>
            <Route path={userSettingsPageDefs.path}>
                <ProtectedRoute pageDef={userSettingsPageDefs}>
                    <ContentDiv>
                        <UserSettings />
                    </ContentDiv>
                </ProtectedRoute>
            </Route>
            <Route path={organizationSettingsPageDefs.path}>
                <ProtectedRoute pageDef={organizationSettingsPageDefs}>
                    <ContentDiv>
                        <OrganizationSettings />
                    </ContentDiv>
                </ProtectedRoute>
            </Route>
            <Route path="*">
                <Redirect to={profile.home_page ? profile.home_page : DEFAULT_PAGE} />
            </Route>
        </Switch>
    );
};

export default Content;
